/**
 * Weekly operating-cycle engine: rollover transitions, procrastination
 * fatigue detection, and shareable summary exports. Pure functions —
 * fully unit-tested, no store or DOM dependencies.
 */

import type { ArchivedTask, Task, WeekRecord, ZoneId, ZoneMeta } from '../types';
import { FATIGUE_THRESHOLD, ZONES } from '../types';

export interface RolloverResult {
  /** Open tasks with their rollover counters incremented. */
  carried: Task[];
  /** The closed week's record for the Mastery log. */
  record: Omit<WeekRecord, 'id' | 'label' | 'endedAt'>;
}

/**
 * The Mastery header's "±N% vs previous week" (18 Oct 2026 fix). The figure
 * shown is THIS week's when anything is finished yet (archive non-empty),
 * else the last closed week's; the comparison must be the week before the
 * one shown. It used weekHistory[1] in both cases, so with work finished
 * this week it compared against the week before last (MEASURED: 50% vs a
 * 42% last week showed "+15%").
 */
export function zone2Delta(current: Record<ZoneId, number>, archiveLength: number, history: WeekRecord[]): number | undefined {
  const previous = archiveLength > 0 ? history[0] : history[1];
  if (!previous) return undefined;
  return current.zone2 - previous.distribution.zone2;
}

/** Percentage distribution (0–100) of completed work across zones. */
export function distributionOf(archive: ArchivedTask[]): Record<ZoneId, number> {
  const dist: Record<ZoneId, number> = { zone1: 0, zone2: 0, zone3: 0, zone4: 0 };
  if (archive.length === 0) return dist;
  for (const t of archive) dist[t.zone] += 1;
  for (const z of ZONES) dist[z.id] = Math.round((dist[z.id] / archive.length) * 100);
  return dist;
}

/**
 * Close the week: completed tasks are dropped (they live on in the archive
 * / week record), open tasks carry forward with rolloverCount + 1.
 */
export function rolloverWeek(tasks: Task[], archive: ArchivedTask[]): RolloverResult {
  const open = tasks.filter((t) => !t.done);
  const carried = open.map((t) => ({ ...t, rolloverCount: t.rolloverCount + 1 }));
  return {
    carried,
    record: {
      completedCount: archive.length,
      rolledOverCount: carried.length,
      distribution: distributionOf(archive),
    },
  };
}

/** Tasks that have survived enough rollovers to demand an audit. */
export function fatiguedTasks(tasks: Task[]): Task[] {
  return tasks.filter((t) => !t.done && t.rolloverCount >= FATIGUE_THRESHOLD);
}

export type ExportFormat = 'keep' | 'slack' | 'teams' | 'email';

/** Group items into (zone, items) pairs in canonical zone order; empty zones omitted. */
function groupByZone<T extends { zone: ZoneId }>(items: T[]): [ZoneMeta, T[]][] {
  return ZONES.map((z) => [z, items.filter((i) => i.zone === z.id)] as [ZoneMeta, T[]]).filter(
    ([, list]) => list.length > 0,
  );
}

/**
 * Build the weekly summary in the requested share format. Items are grouped
 * under zone headers (name + description) so the report reads as a
 * structured briefing, not a flat dump.
 */
export function exportSummary(
  format: ExportFormat,
  tasks: Task[],
  archive: ArchivedTask[],
  weekLabelText = '',
): string {
  const open = tasks.filter((t) => !t.done);
  const slack = format === 'slack';
  const md = format === 'teams';

  const bold = (t: string) => (slack ? `*${t}*` : md ? `**${t}**` : t);
  const italic = (t: string) => (slack || md ? `_${t}_` : t);
  const doneMark = slack ? '  • :white_check_mark: ' : md ? '* ✔️ ' : '   ✓ ';
  const openMark = slack ? '  • :hourglass: ' : md ? '* ⏳ ' : '   → ';

  const lines: string[] = [];
  const title = `ZONEIN AI WEEKLY REPORT${weekLabelText ? ` — ${weekLabelText}` : ''}`;
  lines.push(md ? `### ${title}` : bold(title));

  const section = (
    heading: string,
    items: (ArchivedTask | Task)[],
    mark: string,
    emptyText: string,
  ) => {
    lines.push('');
    lines.push(bold(heading));
    if (items.length === 0) {
      lines.push(italic(emptyText));
      return;
    }
    for (const [zone, zoneItems] of groupByZone(items)) {
      lines.push('');
      lines.push(bold(`${zone.emoji} ${zone.title}`));
      lines.push(italic(zone.subtitle));
      for (const item of zoneItems) lines.push(`${mark}${item.text}`);
    }
  };

  section(`🏆 COMPLETED THIS WEEK (${archive.length})`, archive, doneMark, 'No achievements logged yet.');
  section(`⏳ CARRIED FORWARD (${open.length})`, open, openMark, 'Nothing left open — clean slate!');

  lines.push('');
  lines.push(italic('Generated via ZoneIn AI.'));
  return lines.join('\n');
}
